
import React, { useEffect, useRef } from 'react';

const PaintWaveAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const gl = canvas.getContext('webgl');
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    // Shader sources
    const fragmentShader = `
      precision highp float;
      uniform vec2 u_res;
      uniform float u_time;

      const vec3 p0 = vec3(0.4706, 0.0,    0.0);
      const vec3 p1 = vec3(0.7569, 0.0706, 0.1216);
      const vec3 p2 = vec3(0.9922, 0.9412, 0.8353);
      const vec3 p3 = vec3(0.0,    0.1882, 0.2863);
      const vec3 p4 = vec3(0.4,    0.6078, 0.7373);

      vec3 band(float v){
        return v < 0.2 ? p0 :
               v < 0.4 ? p1 :
               v < 0.6 ? p2 :
               v < 0.8 ? p3 : p4;
      }

      void main(){
        vec2 uv = (gl_FragCoord.xy / u_res - 0.5) * 2.0;
        uv.x *= u_res.x / u_res.y; // keep aspect ratio

        float r = length(uv);
        float t = u_time;

        // LOWER spatial frequencies for broader, calmer ripples
        float val = sin(6.0 * r - t)
                  + sin(3.0 * (uv.x * cos(t) + uv.y * sin(t)))
                  + 0.5 * sin(8.0 * (uv.x * sin(t * 0.5) - uv.y * cos(t * 0.5)));

        val = (val + 3.0) / 6.0;
        gl_FragColor = vec4(band(val), 1.0);
      }
    `;

    const vertexShader = `
      attribute vec2 position;
      void main(){
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    // Create shaders
    const createShader = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    // Create program
    const vertShader = createShader(gl.VERTEX_SHADER, vertexShader);
    const fragShader = createShader(gl.FRAGMENT_SHADER, fragmentShader);
    
    if (!vertShader || !fragShader) return;
    
    const program = gl.createProgram();
    if (!program) return;
    
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);
    
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }
    
    gl.useProgram(program);

    // Create buffer
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1, 1, -1, -1, 1,
        -1, 1, 1, -1, 1, 1
      ]),
      gl.STATIC_DRAW
    );
    
    // Set attributes
    const positionLocation = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const uResLocation = gl.getUniformLocation(program, 'u_res');
    const uTimeLocation = gl.getUniformLocation(program, 'u_time');

    // Handle resize
    const resize = () => {
      if (!canvas || !wrapperRef.current) return;
      
      const dpr = window.devicePixelRatio || 1;
      const rect = wrapperRef.current.getBoundingClientRect();
      const width = Math.round(rect.width * dpr);
      const height = Math.round(rect.height * dpr);
      
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      
      gl.uniform2f(uResLocation, width, height);
    };

    // Add resize event listener
    window.addEventListener('resize', resize);
    resize();

    // Animation loop
    const startTime = performance.now();
    let animationFrameId: number;

    const render = () => {
      gl.uniform1f(uTimeLocation, (performance.now() - startTime) / 2000.0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    // Clean up
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
      gl.deleteProgram(program);
      gl.deleteShader(vertShader);
      gl.deleteShader(fragShader);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return (
    <div 
      ref={wrapperRef} 
      className="w-full h-full relative rounded-2xl overflow-hidden"
      style={{ aspectRatio: '16/9' }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block"></canvas>
    </div>
  );
};

export default PaintWaveAnimation;
